import { Typography, Stack, styled } from "@mui/material";

import { useInvoiceStats } from "@/hooks/use-invoice-stats";
import { InvoicesSummary } from "../data";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { useSearchParams } from "react-router-dom";
import { useInvoices } from "@/hooks/use-invoices";
import { DataTable } from "@/components/custom/data-table";
import CustomPagination from "@/components/custom/custom-pagination";
import { useMemo } from "react";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { InvoiceColumns } from "@/components/invoices/invoice-columns";
import { formatCurrency } from "@/utils/format-currency";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const Invoices = () => {
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";

  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const status = searchParams.get("status");

  const queryParams = {
    page: currentPage,
    size: pageSize,
    status: status ? status : undefined,
    search: searchValue,
  };

  const {
    data: invoices,
    isLoading: isInvoicesLoading,
    isError,
  } = useInvoices(true, queryParams);

  const columns = useMemo(() => InvoiceColumns, []);

  const { stats, isLoading } = useInvoiceStats();

  return (
    <SectionRoot>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {InvoicesSummary.map(({ title, objKeyRepr, icon }, idx) => (
            <CustomSimpleCard
              key={`${objKeyRepr}-${idx}`}
              title={title}
              icon={icon}
              isLoading={isLoading}
            >
              <Typography variant="h6">
                {objKeyRepr === "totalValue"
                  ? formatCurrency(stats?.[objKeyRepr] ?? 0)
                  : (stats?.[objKeyRepr] ?? 0)}
              </Typography>
            </CustomSimpleCard>
          ))}
        </Stack>

        <Card>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <CustomSearchInput
              paramKey="search"
              placeholder="Search by invoice, merchant or customer..."
            />
            <CustomSelectInput
              paramKey="status"
              label="Status"
              placeholder="All Status"
              options={[
                { label: "Settled", value: "SETTLED" },
                { label: "Unsettled", value: "UNSETTLED" },
              ]}
            />
          </Stack>

          <DataTable
            data={invoices?.data ?? []}
            columns={columns}
            isLoading={isInvoicesLoading}
            isError={isError}
            noDataMessage="No invoices found."
          />

          <CustomPagination
            total={invoices?.total ?? 0}
            perPageOptions={[5, 10, 20, 50, 100]}
          />
        </Card>
      </Stack>
    </SectionRoot>
  );
};
